// src/pages/inventory/StockAdjustmentApproval.js
// ZENO ERP — Stock Adjustment Approval
// Approvers: Owner + SM (the stock_adjustments rule); others see the list only
// On Approve → stock + ledger row + status in ONE transaction
// On Reject  → reason saved, stock unchanged
// Both go through stockAdjustmentService.js (P4-6)

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import {
  approveStockAdjustment,
  canApproveAdjustment,
  rejectStockAdjustment,
} from "../../services/stockAdjustmentService";

export default function StockAdjustmentApproval() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const canApprove = canApproveAdjustment(user.role);

  const [adjustments,    setAdjustments]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filter,         setFilter]         = useState("pending");
  const [actionLoading,  setActionLoading]  = useState("");
  const [rejectModal,    setRejectModal]    = useState(null); // holds adjustment doc
  const [rejectReason,   setRejectReason]   = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { if (companyId) fetchAdjustments(); }, [filter]);

  const fetchAdjustments = async () => {
    try {
      setLoading(true);
      const q = filter === "all"
        ? query(collection(db, "stock_adjustments"), where("company_id", "==", companyId))
        : query(collection(db, "stock_adjustments"), where("company_id", "==", companyId), where("status", "==", filter));
      const snap = await getDocs(q);
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.created_at?.seconds ?? 0) - (a.created_at?.seconds ?? 0));
      setAdjustments(list);
    } catch (err) {
      console.error("fetchAdjustments error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── APPROVE ────────────────────────────────────────────
  const handleApprove = async (adj) => {
    if (actionLoading) return;
    setActionLoading(adj.id);
    try {
      await approveStockAdjustment({
        adjustmentId:   adj.id,
        companyId,
        role:           user.role,
        approvedBy:     user.uid || user.id || "",
        approvedByName: user.name || "",
      });

      alert(`✅ ${adj.adjustment_no} approved — stock updated`);
      fetchAdjustments();

    } catch (err) {
      console.error("Approve error:", err);
      alert(err.message || "Approval failed");
      fetchAdjustments();
    } finally {
      setActionLoading("");
    }
  };

  // ── REJECT ─────────────────────────────────────────────
  const handleReject = async () => {
    if (!rejectReason.trim()) { alert("Rejection reason required"); return; }
    if (!rejectModal) return;
    setActionLoading(rejectModal.id);
    try {
      await rejectStockAdjustment({
        adjustmentId:   rejectModal.id,
        companyId,
        role:           user.role,
        rejectedBy:     user.uid || user.id || "",
        rejectedByName: user.name || "",
        reason:         rejectReason,
      });
      alert(`❌ ${rejectModal.adjustment_no} rejected`);
      setRejectModal(null);
      setRejectReason("");
      fetchAdjustments();
    } catch (err) {
      console.error("Reject error:", err);
      alert(err.message || "Rejection failed");
      setRejectModal(null);
      fetchAdjustments();
    } finally {
      setActionLoading("");
    }
  };

  const STATUS_UI = {
    pending:  { label: "Pending",  bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
    approved: { label: "Approved", bg: "#dcfce7", color: "#166534", dot: "#22c55e" },
    rejected: { label: "Rejected", bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  };

  return (
    <div className="p-4 pb-20 bg-gray-50 min-h-screen max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-800 mb-4 block">
        ← Back
      </button>

      <div className="flex justify-between items-center mb-5">
        <h1 className="text-xl font-bold text-slate-800">Stock Adjustment Approvals</h1>
        <button onClick={() => navigate("/stock-adjustment")}
          className="text-xs text-amber-600 font-semibold border border-amber-300 px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 transition">
          + New Adjustment
        </button>
      </div>

      {/* FILTER TABS */}
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition capitalize ${
              filter === f
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-slate-600 border-slate-200"
            }`}>
            {f}
          </button>
        ))}
      </div>

      {/* LIST */}
      {loading ? (
        <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
      ) : adjustments.length === 0 ? (
        <div className="text-center mt-16">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-slate-500">No {filter} adjustments found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {adjustments.map(adj => {
            const ui = STATUS_UI[adj.status] || STATUS_UI.pending;
            return (
              <div key={adj.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
                style={{ borderLeft: `4px solid ${ui.dot}` }}>

                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-bold text-slate-800">{adj.adjustment_no}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {adj.submitted_by_name || "—"} •{" "}
                      {adj.submitted_at?.seconds
                        ? new Date(adj.submitted_at.seconds * 1000).toLocaleDateString("en-IN")
                        : "—"}
                    </p>
                  </div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full"
                    style={{ background: ui.bg, color: ui.color }}>
                    {ui.label}
                  </span>
                </div>

                {/* Item details */}
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div>
                    <p className="text-xs text-slate-400">Item</p>
                    <p className="font-semibold text-slate-700">{adj.item_name}</p>
                    <p className="text-xs text-slate-400">{adj.item_code}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Stock Change</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-slate-500">{adj.current_stock}</span>
                      <span className="text-slate-300">→</span>
                      <span className="font-bold text-slate-800">{adj.new_stock}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        adj.difference > 0
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}>
                        {adj.difference > 0 ? `+${adj.difference}` : adj.difference} {adj.unit}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Reason */}
                <div className="mb-3">
                  <p className="text-xs text-slate-400">Reason</p>
                  <p className="text-sm text-slate-700">{adj.reason}</p>
                  {adj.notes && <p className="text-xs text-slate-400 mt-0.5">{adj.notes}</p>}
                </div>

                {/* Rejection reason */}
                {adj.status === "rejected" && adj.rejection_reason && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-2 mb-3">
                    <p className="text-xs text-red-600">
                      Rejected by {adj.rejected_by_name}: {adj.rejection_reason}
                    </p>
                  </div>
                )}

                {/* Approved info */}
                {adj.status === "approved" && adj.approved_by_name && (
                  <div className="bg-green-50 border border-green-100 rounded-xl p-2 mb-3">
                    <p className="text-xs text-green-600">
                      Approved by {adj.approved_by_name} •{" "}
                      {adj.approved_at?.seconds
                        ? new Date(adj.approved_at.seconds * 1000).toLocaleDateString("en-IN")
                        : ""}
                    </p>
                  </div>
                )}

                {/* Actions — pending only, Owner / SM only */}
                {adj.status === "pending" && !canApprove && (
                  <p className="text-xs text-slate-400">Only Owner or SM can approve</p>
                )}
                {adj.status === "pending" && canApprove && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(adj)}
                      disabled={!!actionLoading}
                      className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-40 transition">
                      {actionLoading === adj.id ? "Processing..." : "✓ Approve"}
                    </button>
                    <button
                      onClick={() => { setRejectModal(adj); setRejectReason(""); }}
                      disabled={!!actionLoading}
                      className="flex-1 py-2.5 rounded-xl bg-red-100 hover:bg-red-200 text-red-600 text-sm font-semibold disabled:opacity-40 transition">
                      ✕ Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* REJECT MODAL */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5">
            <h3 className="font-bold text-slate-800 mb-1">Reject Adjustment</h3>
            <p className="text-xs text-slate-500 mb-3">
              {rejectModal.adjustment_no} — {rejectModal.item_name}
            </p>
            <textarea rows={3} placeholder="Rejection reason *"
              value={rejectReason} onChange={e => setRejectReason(e.target.value)}
              autoFocus
              className="w-full border border-slate-200 rounded-xl p-3 text-sm resize-none outline-none mb-3 focus:ring-2 focus:ring-red-300" />
            <div className="flex gap-3">
              <button onClick={() => { setRejectModal(null); setRejectReason(""); }}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={handleReject} disabled={!!actionLoading}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-semibold text-sm disabled:opacity-40">
                {actionLoading ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}